// src/components/tokenPage/valuation/DauDynamicsCard.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { formatNumber } from "@/utils/charts/formatters";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { useTokenFundamentals } from "@/contexts/token";
import { sliceByRange } from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';

/**
 * DauDynamicsCard - Shows historical Daily Active Users time series
 * 
 * Uses TokenFundamentalsContext for data (no inline fetching).
 */
const DauDynamicsCard: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const { dau, isLoading } = useTokenFundamentals();

  const { dates, values } = useMemo(() => {
    if (dau.dates.length === 0) {
      return { dates: [], values: [] };
    }
    
    const targetLen = range === '90d' ? 90 : range === '180d' ? 180 : range === '365d' ? 365 : dau.dates.length;
    return sliceByRange(dau.dates, dau.values, targetLen);
  }, [dau, range]);

  // Hide if no data available (after loading)
  if (!isLoading && (!values.length || values.every(v => !Number.isFinite(v)))) {
    return null;
  }

  return (
    <DataCard
      title="Daily Active Users"
      subtitle="Activity over time"
      icon="users"
      iconColor="text-amber-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[{ name: 'Daily Active Users', values, color: '#F59E0B', areaStyle: true }]}
        height={260}
        yAxisFormatter={(v) => formatNumber(v, { compact: true })}
        tooltipFormatter={(params) => {
          const items = params as Array<{ axisValue: string; value: number }>;
          if (!items || items.length === 0) return '';
          const item = items[0];
          const dateStr = item?.axisValue || '';
          const formattedDate = formatDateLabel(dateStr, 'medium');
          const val = Number(item?.value ?? 0);
          const formattedValue = Number.isFinite(val) ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : 'N/A';
          return `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${formattedDate}</div><div style="font-weight:600;color:#f3f4f6;">${formattedValue}</div>`;
        }}
        showLegend={false}
      />
    </DataCard>
  );
};

export default DauDynamicsCard;
